import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Users, UserPlus, Mail, Crown, Shield, User, Trash2, Clock, Loader2, Send, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { useUsageLimits } from "@/hooks/useUsageLimits";
import { UpgradePrompt } from "@/components/usage/UpgradePrompt";

interface TeamMember {
  id: string;
  user_id: string;
  role: "owner" | "admin" | "member";
  profile: {
    id: string;
    full_name: string | null;
    email: string;
    avatar_url: string | null;
  } | null;
}

interface Invitation {
  id: string;
  email: string;
  role: "owner" | "admin" | "member";
  created_at: string;
  expires_at: string;
}

interface TeamManagementProps {
  organizationId: string | null;
  currentUserId: string;
}

const emailSchema = z.string().email("Adresse email invalide");

const roleLabels: Record<string, string> = {
  owner: "Propriétaire",
  admin: "Administrateur",
  member: "Membre",
};

const roleIcons: Record<string, React.ElementType> = {
  owner: Crown,
  admin: Shield,
  member: User,
};

const roleBadgeVariants: Record<string, "default" | "secondary" | "outline"> = {
  owner: "default",
  admin: "secondary",
  member: "outline",
};

const TeamManagement = ({ organizationId, currentUserId }: TeamManagementProps) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [sending, setSending] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<"owner" | "admin" | "member" | null>(null);
  const { canUseFeature } = useUsageLimits();

  useEffect(() => {
    if (organizationId) {
      loadTeamData();
    } else {
      setLoading(false);
    }
  }, [organizationId]);

  const loadTeamData = async () => {
    if (!organizationId) return;

    setLoading(true);

    // Load team members with their profiles
    const { data: rolesData, error: rolesError } = await supabase
      .from("user_roles")
      .select("id, user_id, role")
      .eq("organization_id", organizationId);

    if (rolesError) {
      console.error("Error loading roles:", rolesError);
      setLoading(false);
      return;
    }

    // Get profiles for each user
    const memberPromises = (rolesData || []).map(async (role) => {
      const { data: profile } = await supabase
        .from("profiles")
        .select("id, full_name, email, avatar_url")
        .eq("id", role.user_id)
        .maybeSingle();

      return {
        ...role,
        profile,
      } as TeamMember;
    });

    const membersWithProfiles = await Promise.all(memberPromises);
    setMembers(membersWithProfiles);

    // Find current user's role
    const currentRole = membersWithProfiles.find(m => m.user_id === currentUserId);
    setCurrentUserRole(currentRole?.role || null);

    // Load pending invitations
    const { data: invitesData } = await supabase
      .from("team_invitations")
      .select("id, email, role, created_at, expires_at")
      .eq("organization_id", organizationId);

    setInvitations((invitesData || []) as Invitation[]);
    setLoading(false);
  };

  const handleInvite = async () => {
    // Validate email
    const validation = emailSchema.safeParse(inviteEmail.trim());
    if (!validation.success) {
      toast.error("Adresse email invalide");
      return;
    }

    if (!organizationId) return;

    setSending(true);

    // Check if already a member
    const existingMember = members.find(
      m => m.profile?.email.toLowerCase() === inviteEmail.toLowerCase()
    );
    if (existingMember) {
      toast.error("Cette personne est déjà membre de l'équipe");
      setSending(false);
      return;
    }

    // Check if already invited
    const existingInvite = invitations.find(
      i => i.email.toLowerCase() === inviteEmail.toLowerCase()
    );
    if (existingInvite) {
      toast.error("Une invitation a déjà été envoyée à cette adresse");
      setSending(false);
      return;
    }

    const { error } = await supabase
      .from("team_invitations")
      .insert({
        organization_id: organizationId,
        email: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
        invited_by: currentUserId,
      });

    if (error) {
      console.error("Error sending invitation:", error);
      toast.error("Erreur lors de l'envoi de l'invitation");
      setSending(false);
      return;
    }

    toast.success(`Invitation envoyée à ${inviteEmail}`);
    setInviteEmail("");
    setInviteRole("member");
    setInviteDialogOpen(false);
    setSending(false);
    loadTeamData();
  };

  const handleCancelInvitation = async (invitationId: string) => {
    const { error } = await supabase
      .from("team_invitations")
      .delete()
      .eq("id", invitationId);

    if (error) {
      toast.error("Erreur lors de l'annulation");
      return;
    }

    toast.success("Invitation annulée");
    setInvitations(prev => prev.filter(i => i.id !== invitationId));
  };

  const handleUpdateRole = async (memberId: string, newRole: "admin" | "member") => {
    const { error } = await supabase
      .from("user_roles")
      .update({ role: newRole })
      .eq("id", memberId);

    if (error) {
      toast.error("Erreur lors de la mise à jour du rôle");
      return;
    }

    toast.success("Rôle mis à jour");
    setMembers(prev =>
      prev.map(m => (m.id === memberId ? { ...m, role: newRole } : m))
    );
  };

  const handleRemoveMember = async (memberId: string, userId: string) => {
    // Remove role
    const { error: roleError } = await supabase
      .from("user_roles")
      .delete()
      .eq("id", memberId);

    if (roleError) {
      toast.error("Erreur lors de la suppression");
      return;
    }

    // Update profile to remove org
    await supabase
      .from("profiles")
      .update({ organization_id: null })
      .eq("id", userId);

    toast.success("Membre retiré de l'équipe");
    setMembers(prev => prev.filter(m => m.id !== memberId));
  };

  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name
        .split(" ")
        .map(n => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };

  const canManageTeam = currentUserRole === "owner" || currentUserRole === "admin";

  // Check if team feature is available
  if (!canUseFeature("team")) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            <CardTitle>Équipe</CardTitle>
          </div>
          <CardDescription>Gérez les membres de votre organisation</CardDescription>
        </CardHeader>
        <CardContent>
          <UpgradePrompt 
            feature="Gestion d'équipe" 
            description="Invitez des collaborateurs et gérez les permissions de votre équipe. Disponible avec le plan Pro."
            minPlan="Pro"
          />
        </CardContent>
      </Card>
    );
  }

  if (!organizationId) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            <CardTitle>Équipe</CardTitle>
          </div>
          <CardDescription>Gérez les membres de votre organisation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">
              La gestion d'équipe est disponible pour les comptes équipe.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Passez à un compte équipe depuis l'onboarding pour inviter des collaborateurs.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            <CardTitle>Équipe</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            <CardTitle>Équipe</CardTitle>
          </div>
          {canManageTeam && (
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Inviter
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Inviter un membre</DialogTitle>
                  <DialogDescription>
                    Envoyez une invitation par email pour rejoindre votre équipe.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-email">Email</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="collegue@entreprise.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Rôle</Label>
                    <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as "admin" | "member")}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Membre
                          </div>
                        </SelectItem>
                        <SelectItem value="admin">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            Administrateur
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Les administrateurs peuvent inviter et gérer les membres.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleInvite} disabled={sending || !inviteEmail}>
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Envoyer l'invitation
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        <CardDescription>
          {members.length} membre{members.length > 1 ? "s" : ""}
          {invitations.length > 0 && ` • ${invitations.length} invitation${invitations.length > 1 ? "s" : ""} en attente`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Team Members */}
        <div className="space-y-3">
          {members.map((member) => {
            const RoleIcon = roleIcons[member.role];
            const isCurrentUser = member.user_id === currentUserId;
            const canModify = canManageTeam && !isCurrentUser && member.role !== "owner";

            return (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 rounded-lg bg-secondary/30"
              >
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={member.profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {getInitials(member.profile?.full_name || null, member.profile?.email || "")}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      {member.profile?.full_name || member.profile?.email}
                      {isCurrentUser && (
                        <Badge variant="outline" className="text-xs">Vous</Badge>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">{member.profile?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canModify ? (
                    <Select
                      value={member.role}
                      onValueChange={(v) => handleUpdateRole(member.id, v as "admin" | "member")}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Membre</SelectItem>
                        <SelectItem value="admin">Administrateur</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant={roleBadgeVariants[member.role]}>
                      <RoleIcon className="w-3 h-3 mr-1" />
                      {roleLabels[member.role]}
                    </Badge>
                  )}
                  {canModify && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Retirer ce membre ?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {member.profile?.full_name || member.profile?.email} sera retiré de l'équipe
                            et n'aura plus accès aux documents partagés.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => handleRemoveMember(member.id, member.user_id)}
                          >
                            Retirer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pending Invitations */}
        {invitations.length > 0 && (
          <>
            <Separator />
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Invitations en attente
              </h4>
              <div className="space-y-2">
                {invitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-dashed bg-secondary/20"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">{invitation.email}</p>
                        <p className="text-xs text-muted-foreground">
                          Expire le {new Date(invitation.expires_at).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{roleLabels[invitation.role]}</Badge>
                      {canManageTeam && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => handleCancelInvitation(invitation.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamManagement;